import React, { useEffect, useRef, useState } from 'react'
import { FaReply } from 'react-icons/fa6'
import { useOrder } from '../../hooks/useAuth'
import { infoToast } from '../../utils/firebase/firebase.utils'
import './reply-dialog.styles.scss'

const DEFAULT_FROM_EMAIL =
  process.env.REACT_APP_MAIL_FROM_EMAIL || 'cakebysandy01@gmail.com'

const ReplyDialog = ({ inquiry }) => {
  const { updateInquiry } = useOrder()
  const [open, setOpen] = useState(false)
  const [reply, setReply] = useState('')
  const [useDifferentEmail, setUseDifferentEmail] = useState(false)
  const [fromEmail, setFromEmail] = useState(DEFAULT_FROM_EMAIL)
  const [submitting, setSubmitting] = useState(false)
  const replyRef = useRef(null)

  const openDialog = () => {
    setReply('')
    setUseDifferentEmail(false)
    setFromEmail(DEFAULT_FROM_EMAIL)
    setOpen(true)
  }

  const close = () => {
    setOpen(false)
    setReply('')
    setUseDifferentEmail(false)
    setFromEmail(DEFAULT_FROM_EMAIL)
  }

  useEffect(() => {
    if (!open) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') close()
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)

    const focusTimer = window.setTimeout(() => {
      replyRef.current?.focus()
    }, 50)

    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKeyDown)
      window.clearTimeout(focusTimer)
    }
  }, [open])

  const updateReply = async (event) => {
    event.preventDefault()

    if (!reply.trim()) {
      return infoToast('Please write a reply before sending.')
    }

    if (!inquiry?.id) {
      return infoToast('This inquiry is missing an id and cannot be updated.')
    }

    const resolvedFrom = useDifferentEmail ? fromEmail.trim() : DEFAULT_FROM_EMAIL

    if (useDifferentEmail) {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resolvedFrom)
      if (!emailOk) {
        return infoToast('Enter a valid From email address.')
      }
    }

    setSubmitting(true)

    const payload = {
      ...inquiry,
      status: 'yes',
      inq_response: reply.trim(),
      replied_by: resolvedFrom,
      reply_from: resolvedFrom,
    }

    try {
      await updateInquiry(payload)
      close()
    } catch (error) {
      console.error('Reply failed:', error)
      infoToast('Could not send reply. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <button type="button" className="reply-v2__open" onClick={openDialog}>
        <FaReply size={13} aria-hidden="true" />
        Reply
      </button>

      {open ? (
        <div className="reply-v2" role="dialog" aria-modal="true" aria-label="Reply to inquiry">
          <div className="reply-v2__backdrop" onClick={close} aria-hidden="true" />
          <div
            className="reply-v2__panel"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="reply-v2__head">
              <div>
                <p className="reply-v2__eyebrow">Reply</p>
                <h3>{inquiry?.name || 'Customer'}</h3>
                <p>{inquiry?.email}</p>
              </div>
              <button type="button" className="reply-v2__close" onClick={close}>
                Close
              </button>
            </div>

            <div className="reply-v2__original">
              <p className="reply-v2__label">Original message</p>
              <p>{inquiry?.message}</p>
            </div>

            <form onSubmit={updateReply} className="reply-v2__form">
              <div className="reply-v2__field">
                <span>From</span>
                <input
                  type={useDifferentEmail ? 'email' : 'text'}
                  readOnly={!useDifferentEmail}
                  tabIndex={useDifferentEmail ? 0 : -1}
                  className={useDifferentEmail ? undefined : 'reply-v2__from'}
                  value={useDifferentEmail ? fromEmail : DEFAULT_FROM_EMAIL}
                  onChange={(event) => setFromEmail(event.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </div>

              <label className="reply-v2__check">
                <input
                  type="checkbox"
                  checked={useDifferentEmail}
                  onChange={(event) => {
                    const checked = event.target.checked
                    setUseDifferentEmail(checked)
                    setFromEmail(checked ? '' : DEFAULT_FROM_EMAIL)
                  }}
                />
                <span>Use different email</span>
              </label>

              <div className="reply-v2__field">
                <label htmlFor={`reply-${inquiry?.id || 'new'}`}>Your reply</label>
                <textarea
                  id={`reply-${inquiry?.id || 'new'}`}
                  ref={replyRef}
                  name="reply_txt"
                  rows={5}
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  placeholder="Write a clear, friendly reply..."
                  required
                  autoComplete="off"
                />
              </div>

              <div className="reply-v2__actions">
                <button type="button" className="reply-v2__cancel" onClick={close}>
                  Cancel
                </button>
                <button type="submit" className="reply-v2__send" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send reply'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default ReplyDialog
